<template>
	<v-dialog
			:value="isViewNotifications"
			content-class="dialogWrapper"
			fullscreen
			hide-overlay
			transition="dialog-bottom-transition"
	>
		<div class="dialogWrap">
			<div class="dialogTitle">
				<span>{{ dialogFlag === 'edit' ? 'Edit notification' : 'Create notification' }}</span>
				<button type="button" @click="onClose">
					<svg-sprite width="20" height="20" icon-id="close"></svg-sprite>
				</button>
			</div>

			<div class="dialogMain">
				<div class="main">
					<div class="gr">
						<field
								label="Title"
								placeholder="Enter title"
								width="100%"
								margin="0"
								:setInputValue="title"
								:class-error="getError('title')"
								text-error="Title is required and must be less than 255 characters"
								:isRequired="true"
								@updateInput="onInput($event, 'title')"
						></field>

						<field
								label="Publication period"
								placeholder="Select period"
								width="100%"
								margin="0"
								:isDateRange="true"
								:dateValue="publicationPeriod"
								:class-error="getError('publicationPeriod')"
								text-error="Publication period is required"
								:isRequired="true"
								@updateDate="onDateRange"
						></field>

						<field
								label="Department"
								placeholder="Select department"
								width="100%"
								margin="0"
								:isMultiple="true"
								:isBtnAll="true"
								:items="roleList"
								:multiValue="departments"
								item-text="value"
								item-value="id"
								:class-error="getError('departments')"
								text-error="Select at least one department"
								:isRequired="true"
								@updateMultiSelect="onDepartmentsSelect"
						></field>
					</div>

					<div v-if="departments.length" class="chipsBlock">
						<div class="chipsLabel">Selected departments</div>

						<div class="chipsRow">
							<v-chip
									v-for="item in departments"
									:key="item.id"
									close
									small
									class="departmentChip"
									@click:close="onRemoveDepartment(item)"
							>
								{{ item.value }}
							</v-chip>
						</div>
					</div>

					<div class="item descriptionWrapper">
						<div class="label required">📝 Changelog description</div>

						<div class="description" :class="{ errorEditor: getError('msg') }">
							<vue-editor
									ref="editor"
									v-model="msg"
									placeholder="Enter description"
									:key="msgKey"
							/>
						</div>

						<div v-if="getError('msg')" class="textError">
							Description is required
						</div>
					</div>
				</div>

				<div class="footer">
					<custom-btn
							text="Save"
							@updateEvent="onSave"
							custom-class="apply"
							is-all-elements
							margin="0"
							:icon-attrs="{ width: 20, height: 20, iconId: 'safe' }"
					></custom-btn>

					<custom-btn
							text="Close"
							@updateEvent="onClose"
							is-all-elements
							margin="0"
							:icon-attrs="{ width: 20, height: 20, iconId: 'close' }"
					></custom-btn>
				</div>
			</div>
		</div>

		<preload v-if="isPreloader"></preload>
	</v-dialog>
</template>

<script>
import Vue from 'vue';
import VueMask from 'v-mask';
Vue.use(VueMask);

import { validationMixin } from "vuelidate";
import { required, maxLength } from "vuelidate/lib/validators";
import { VueEditor } from "vue2-editor";

import Preload from "@/shared/Preload/Preload.vue";
import { mapActions } from "vuex";

export default {
	name: "ViewNotifications",
	components: {
		Preload,
		VueEditor,
	},
	mixins: [ validationMixin ],
	props: {
		dialogFlag: {
			type: String,
			default: 'add'
		},
		editNotifications: {
			type: Object,
			default: function() {
				return null;
			}
		},
	},

	validations: {
		title: {
			required,
			maxLength: maxLength(255),
		},
		publicationPeriod: {
			required,
			fullRange(value) {
				return Array.isArray(value) && value.length === 2 && !!value[0] && !!value[1];
			}
		},
		msg: {
			required,
			notEmptyHtml(value) {
				if (!value) return false;

				const clearedValue = value
					.replace(/<p><br><\/p>/g, '')
					.replace(/<[^>]*>/g, '')
					.replace(/&nbsp;/g, '')
					.trim();

				return !!clearedValue;
			}
		},
		departments: {
			required,
			notEmpty(value) {
				return Array.isArray(value) && value.length > 0;
			}
		}
	},

	computed: {
		getToDateError() {
			if (!this.$v || !this.$v.to || !this.$v.dateRange) return '';

			if (!this.$v.to.$error && !this.$v.dateRange.$error) return '';

			if (this.$v.to.$error) {
				return 'Publication date to is required';
			}

			if (this.$v.dateRange.$error) {
				return 'Date "to" must be greater than or equal to date "from"';
			}

			return '';
		},

		items() {
			return this.roleList.map(function(item) {
				return {
					id: Number(item.id),
					value: item.name
				};
			});
		},
	},

	data: () => ({
		isViewNotifications: true,
		isPreloader: false,
		title: '',
		publicationPeriod: [],
		departments: [],
		roleList: [],
		msgKey: 0,
		msg: '',
		id: null,
	}),

	methods: {
		...mapActions([ 'getRoles' ]),

		onDateRange(value) {
			this.publicationPeriod = value || [];
		},

		getError(key) {
			return this.$v && this.$v[key] && this.$v[key].$error;
		},

		onRemoveDepartment(item) {
			this.departments = this.departments.filter(function(department) {
				return Number(department.id) !== Number(item.id);
			});
		},

		setDefaultData() {
			const { row, department, department_row } = this.editNotifications;

			this.roleList = department || [];

			if (this.dialogFlag === 'add') {
				this.isPreloader = true;
				this.title = '';
				this.publicationPeriod = [];
				this.departments = [];
				this.msg = '';
				this.id = null;
				this.isPreloader = false;
				return;
			}

			this.id = row.id || null;
			this.title = row.title || '';

			if (row.from || row.to) {
				this.publicationPeriod = [
					row.from || '',
					row.to || row.from || ''
				].filter(Boolean);
			}

			if (Array.isArray(department_row) && department_row.length) {
				this.departments = this.getSelectedDepartments(department_row);
			} else {
				this.departments = [];
			}

			const msg = row.message || '';

			this.msg = '';
			this.msgKey++;

			this.$nextTick(() => {
				const editor = this.$refs.editor?.quill;
				if (editor && msg) {
					editor.setContents(editor.clipboard.convert(msg));
				}
			});
		},

		getSelectedDepartments(list) {
			const departmentIds = list.map(function(item) {
				return Number(item);
			});

			return this.roleList.filter(function(item) {
				return departmentIds.includes(Number(item.id));
			});
		},

		onInput({ value }, field) {
			this[field] = value;
		},

		onDate(value, field) {
			this[field] = value;
		},
		
		onDepartmentsSelect({ value }) {
			this.departments = value || [];
		},

		getPayload() {
			const message = this.$refs.editor?.quill?.root?.innerHTML || '';

			return {
				id: this.id,
				title: this.title,
				from: this.publicationPeriod[0] || '',
				to: this.publicationPeriod[1] || this.publicationPeriod[0] || '',
				message,
				departments: this.departments.map(function(item) {
					return Number(item.id);
				}),
			};
		},

		async onSave() {
			this.isPreloader = true;

			this.msg = this.$refs.editor?.quill?.root?.innerHTML || '';
			this.$v.$touch();

			if (!this.$v.$error) {
				try {
					const payload = this.getPayload();

					if (this.dialogFlag === 'edit') {
						// this.$emit('updateEdit', payload);
						this.$emit('updateSave', payload);
					} else {
						this.$emit('updateSave', payload);
					}

					this.$emit('updateClose');
					this.$emit('updateList');
				} catch (e) {
					console.error(e);
				}
			}

			this.isPreloader = false;
		},

		onClose() {
			this.$emit('updateClose');
		},

		async getActionRole() {
			const { userGroup } = await this.getRoles();
			this.roleList = userGroup || [];
		},
	},

	mounted() {
		this.setDefaultData();
	},
};
</script>

<style scoped lang="scss">
@import "../ViewVersion/ViewVersion";

.dateRow {
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
	margin-bottom: 16px;
}

.main {
	padding: 10px;
	background: var(--card-bg-opacity);
	.gr {
		display: grid;
		grid-template-columns: repeat(3, 1fr);
		grid-gap: 10px;
	}
}

.footer {
	display: flex;
	justify-content: flex-end;
	gap: 10px;
	padding: 20px;
	border-top: 1px solid #E5E5E5;
}

@media screen and (max-width: 768px) {
	.dateRow {
		grid-template-columns: 1fr;
	}
}

.chipsRow {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 12px;
}

.departmentChip {
	max-width: 100%;
}
.departmentChip {
	background: var(--card-bg) !important;
	color: var(--card-color) !important;
	border: 1px solid var(--card-color);

	&:hover {
		background: var(--card-bg-opacity) !important;
	}
}

.departmentChip .v-chip__close {
	color: var(--card-color-sub-title);

	&:hover {
		color: var(--card-bg-opacity);
	}
}
.chipsRow {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-bottom: 12px;
	max-height: 80px;
	overflow-y: auto;
	padding-right: 4px;
}
.chipsLabel {
	font-size: 12px;
	color: var(--card-color-sub-title);
	margin-bottom: 6px;
}
.errorEditor {
	border: 1px solid var(--base-red);
	border-radius: 4px;
}

.textError {
	font-size: 12px;
	line-height: 16px;
	color: var(--base-red);
	margin-top: 4px;
}
</style>
